/**
 * Tenant Context Boot
 * Resolves organisation, catalog, and settings from the backend on first load.
 * 
 * Website mode: hostname → resolve-by-domain → full context
 * Endless Aisle mode: screen_id → resolve-by-screen → full context
 * 
 * This replaces the hardcoded DOMAIN_CONFIG map.
 */

// Empty VITE_RCC_BASE means use relative paths (same-origin via Vite proxy)
const API_BASE = import.meta.env.VITE_RCC_BASE || '';
const IS_DEV = import.meta.env.DEV;

let _context = null;
let _bootPromise = null;
let _bootFailed = false;

/**
 * Boot the tenant context. Call once on app startup.
 * Returns the resolved context or null if resolution fails.
 */
export function boot() {
  if (_bootPromise) return _bootPromise;

  const params = new URLSearchParams(window.location.search);
  const isKiosk = params.get('kiosk') === '1';
  const screenId = params.get('screen_id');

  if (isKiosk && screenId) {
    _bootPromise = resolveByScreen(screenId);
  } else {
    _bootPromise = resolveByDomain(window.location.host);
  }

  return _bootPromise;
}

/** True if resolution failed and we're in production (no fallback). */
export function isBootFailed() {
  return _bootFailed;
}

async function resolveByDomain(domain) {
  try {
    const res = await fetch(`${API_BASE}/rcc-api/catalog/resolve-by-domain?domain=${encodeURIComponent(domain)}`);
    if (!res.ok) {
      return handleResolutionFailure('domain', domain);
    }
    const data = await res.json();
    _context = {
      orgId: data.org_id,
      catalogId: data.catalog_id,
      catalogName: data.catalog_name,
      storeId: data.settings?.store_id || null,
      domain: data.domain,
      slug: data.slug,
      status: data.status,
      settings: data.settings || {},
      mode: 'website',
      orderSource: 'website',
      websiteId: domain,
    };
    return _context;
  } catch (err) {
    console.error('[boot] Domain resolution failed:', err);
    return handleResolutionFailure('domain', domain);
  }
}

async function resolveByScreen(screenId) {
  try {
    const res = await fetch(`${API_BASE}/rcc-api/catalog/resolve-by-screen?screen_id=${encodeURIComponent(screenId)}`);
    if (!res.ok) {
      return handleResolutionFailure('screen', screenId);
    }
    const data = await res.json();
    _context = {
      orgId: data.org_id,
      catalogId: data.catalog_id,
      catalogName: data.catalog_name,
      storeId: data.store_id || null,
      screenId: data.screen_id,
      screenName: data.screen_name,
      location: data.location,
      idleTimeoutSeconds: data.idle_timeout_seconds,
      adPlacement: data.ad_placement,
      adHeightPercent: data.ad_height_percent,
      resolution: data.resolution,
      settings: data.settings || {},
      catalogSettings: data.catalog_settings || {},
      mode: 'kiosk',
      orderSource: 'kiosk',
      websiteId: screenId,
    };
    return _context;
  } catch (err) {
    console.error('[boot] Screen resolution failed:', err);
    return handleResolutionFailure('screen', screenId);
  }
}

function handleResolutionFailure(type, identifier) {
  console.error(`[boot] RESOLUTION FAILED: ${type}=${identifier} not resolved from backend`);
  
  if (IS_DEV) {
    // DEV MODE ONLY: Allow URL param override (requires explicit org_id in URL)
    const params = new URLSearchParams(window.location.search);
    const explicitOrgId = params.get('org_id');
    
    if (explicitOrgId) {
      console.warn(`[boot] DEV MODE: Using explicit URL param org_id=${explicitOrgId}`);
      _context = buildDevFallback(explicitOrgId, params);
      return _context;
    }
    
    // No org_id provided - cannot continue even in dev mode
    console.error('[boot] DEV MODE: No org_id in URL params. Cannot proceed.');
    _bootFailed = true;
    _context = null;
    return null;
  }
  
  // Production: do NOT trust client-supplied params, NEVER fallback to hardcoded values
  _bootFailed = true;
  _context = null;
  return null;
}

function buildDevFallback(orgId, params) {
  // Only called in dev mode with explicit org_id from URL
  return {
    orgId: orgId,  // From URL param, no hardcoded fallback
    catalogId: params.get('catalog_id') || null,
    storeId: params.get('store_id') || null,  // No hardcoded S1
    mode: params.get('kiosk') === '1' ? 'kiosk' : 'website',
    orderSource: params.get('kiosk') === '1' ? 'kiosk' : 'website',
    websiteId: window.location.host,
    settings: {},
  };
}

/** Get the resolved context (null before boot completes). */
export function getContext() {
  return _context;
}

/** Convenience accessors that read from resolved context or fallback to URL params. */
export function getOrgId() {
  // NO hardcoded fallback - if _context is null, boot failed
  if (!_context) {
    console.error('[getOrgId] Boot failed or not complete - no org_id available');
    return null;
  }
  return _context.orgId;
}

export function getCatalogId() {
  return _context?.catalogId || null;
}

/** Trusted physical-screen identifier for kiosk mode only (null for website). */
export function getScreenId() {
  return _context?.screenId || null;
}

export function getStoreId() {
  // NO hardcoded S1 fallback
  if (!_context) {
    console.error('[getStoreId] Boot failed or not complete - no store_id available');
    return null;
  }
  return _context.storeId;
}

export function getOrderSource() {
  return _context?.orderSource || (new URLSearchParams(window.location.search).get('kiosk') === '1' ? 'kiosk' : 'website');
}

export function getWebsiteId() {
  return _context?.websiteId || window.location.host;
}

export function getMode() {
  return _context?.mode || (new URLSearchParams(window.location.search).get('kiosk') === '1' ? 'kiosk' : 'website');
}
